import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import { useTheme } from '@mui/material/styles';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { TReviewFilterMode } from '@onesource/schemas';
import { FC, ReactNode, useRef } from 'react';

import { ReviewFilterTrigger } from './review-filter-trigger';

interface ReviewFilterShellProps {
  label: string;
  icon: ReactNode;
  /** What the button shows as picked; empty when the filter is off. */
  selectedLabels: string[];
  mode: TReviewFilterMode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The panel. */
  children: ReactNode;
}

/**
 * A review filter's button and where its panel opens: a popover on desktop, a
 * bottom sheet on a phone (thumb reach, room for the list).
 */
export const ReviewFilterShell: FC<ReviewFilterShellProps> = ({
  label,
  icon,
  selectedLabels,
  mode,
  open,
  onOpenChange,
  children,
}) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('md'));
  const anchorRef = useRef<HTMLButtonElement>(null);
  const close = () => onOpenChange(false);

  return (
    <>
      <ReviewFilterTrigger
        ref={anchorRef}
        label={label}
        icon={icon}
        selectedLabels={selectedLabels}
        mode={mode}
        open={open}
        onClick={() => onOpenChange(!open)}
      />
      {isPhone ? (
        <SwipeableDrawer
          anchor="bottom"
          open={open}
          onOpen={() => onOpenChange(true)}
          onClose={close}
          disableSwipeToOpen
          PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '80dvh' } }}
        >
          {/* Grab handle: tells the user the sheet swipes down to close. */}
          <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: '#d9d4d2', mx: 'auto', mt: 1 }} />
          {children}
        </SwipeableDrawer>
      ) : (
        <Popover
          open={open}
          anchorEl={anchorRef.current}
          onClose={close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: {
                mt: 0.75,
                width: 340,
                maxHeight: 480,
                borderRadius: '14px',
                boxShadow: '0 12px 32px rgba(0,0,0,0.14)',
              },
            },
          }}
        >
          {children}
        </Popover>
      )}
    </>
  );
};
